import React, { useState } from 'react';
import { Truck, Edit3, X, Check, AlertTriangle, Info } from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from '../ui/simple-toaster';

// GVM threshold for FRHV classification (12 tonnes)
const FRHV_GVM_THRESHOLD_KG = 12000;

// Determine vehicle class based on GVM
const getVehicleClassFromGVM = (gvm) => {
  if (!gvm || gvm <= 0) return 'LV';
  if (gvm >= FRHV_GVM_THRESHOLD_KG) return 'FRHV';
  if (gvm >= 4500) return 'HV';
  return 'LV';
};

// Determine compliance regime based on GVM
const getRegimeFromGVM = (gvm) => {
  if (!gvm) return { regime: 'FRHV', label: 'FRHV Compliance (default)', color: 'amber' };
  if (gvm >= FRHV_GVM_THRESHOLD_KG) {
    return { regime: 'FRHV', label: 'FRHV Compliance (HVN Law)', color: 'amber' };
  }
  return { regime: 'BUSINESS', label: 'Business Rules', color: 'blue' };
};

const VehiclesPanel = ({ vehicles, onRefresh }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [form, setForm] = useState({ rego: '', gvm_kg: '', gcm_kg: '', make: '', model: '', primary_production_eligible: false });
  const [editForm, setEditForm] = useState({ gvm_kg: '', gcm_kg: '', make: '', model: '', primary_production_eligible: false });
  const [loading, setLoading] = useState(false);

  // Auto-calculate vehicle class from GVM
  const formVehicleClass = getVehicleClassFromGVM(parseInt(form.gvm_kg) || 0);
  const formRegime = getRegimeFromGVM(parseInt(form.gvm_kg) || 0);
  const editVehicleClass = getVehicleClassFromGVM(parseInt(editForm.gvm_kg) || 0);
  const editRegime = getRegimeFromGVM(parseInt(editForm.gvm_kg) || 0);
  
  // Calculate towing regime if GCM is set
  const formTowingRegime = form.gcm_kg ? getRegimeFromGVM(parseInt(form.gcm_kg) || 0) : null;
  const editTowingRegime = editForm.gcm_kg ? getRegimeFromGVM(parseInt(editForm.gcm_kg) || 0) : null;

  const handleAdd = async (e) => {
    e.preventDefault();
    
    const gvm = parseInt(form.gvm_kg) || 0;
    if (gvm <= 0) {
      toast.error('Please enter a valid GVM (weight in kg)');
      return;
    }
    
    setLoading(true);
    try {
      await api('/vehicles/other', {
        method: 'POST',
        body: JSON.stringify({
          rego: form.rego,
          vehicle_class: getVehicleClassFromGVM(gvm),
          gvm_kg: gvm,
          make: form.make,
          model: form.model
        })
      });
      setShowAdd(false);
      setForm({ rego: '', gvm_kg: '', make: '', model: '' });
      onRefresh();
      toast.success('Vehicle added');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setEditForm({
      gvm_kg: vehicle.gvm_kg || vehicle.weight_kg || '',
      gcm_kg: vehicle.gcm_kg || '',
      make: vehicle.make || '',
      model: vehicle.model || '',
      primary_production_eligible: vehicle.primary_production_eligible || false
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!editingVehicle) return;
    
    const gvm = parseInt(editForm.gvm_kg) || 0;
    const gcm = parseInt(editForm.gcm_kg) || null;
    
    // Validate GCM > GVM if set
    if (gcm && gcm < gvm) {
      toast.error('GCM (towing weight) must be greater than GVM');
      return;
    }
    
    // Primary Production only for FRHV
    if (editForm.primary_production_eligible && gvm < FRHV_GVM_THRESHOLD_KG) {
      toast.error('Primary Production is only available for FRHV vehicles (GVM ≥ 12t)');
      return;
    }
    
    setLoading(true);
    try {
      await api(`/vehicles/${editingVehicle.vehicle_id}`, {
        method: 'PUT',
        body: JSON.stringify({
          vehicle_class: getVehicleClassFromGVM(gvm),
          gvm_kg: gvm,
          gcm_kg: gcm,
          make: editForm.make,
          model: editForm.model,
          primary_production_eligible: editForm.primary_production_eligible
        })
      });
      setEditingVehicle(null);
      onRefresh();
      toast.success('Vehicle updated');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h2 className="text-lg sm:text-xl font-semibold text-white">Vehicles</h2>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg transition text-sm sm:text-base"
          data-testid="add-vehicle-btn"
        >
          Add Vehicle
        </button>
      </div>

      {/* GVM Information Note */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-slate-300">
            <p className="font-medium text-blue-400 mb-1">Vehicle Classification by GVM (Gross Vehicle Mass)</p>
            <ul className="text-xs text-slate-400 space-y-1">
              <li>• <span className="text-amber-400">FRHV</span>: GVM ≥ 12,000 kg → Full HVN Law compliance required</li>
              <li>• <span className="text-purple-400">HV</span>: GVM 4,500 - 11,999 kg → Business Rules apply</li>
              <li>• <span className="text-blue-400">LV</span>: GVM &lt; 4,500 kg → Business Rules apply</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Edit Vehicle Modal */}
      {editingVehicle && (
        <div className="bg-slate-800/50 rounded-2xl border border-blue-500/50 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-medium text-white">
              Edit Vehicle: {editingVehicle.rego || editingVehicle.registration}
            </h3>
            <button onClick={() => setEditingVehicle(null)} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleEdit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">GVM (kg)</label>
                <input
                  type="number"
                  value={editForm.gvm_kg}
                  onChange={(e) => setEditForm({ ...editForm, gvm_kg: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white"
                  placeholder="e.g., 15000"
                  min="0"
                  max="100000"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">GCM (kg) - Towing</label>
                <input
                  type="number"
                  value={editForm.gcm_kg}
                  onChange={(e) => setEditForm({ ...editForm, gcm_kg: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white"
                  placeholder="e.g., 22000"
                  min="0"
                  max="100000"
                />
                <p className="text-xs text-slate-500 mt-1">Gross Combination Mass when towing</p>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Make</label>
                <input
                  type="text"
                  value={editForm.make}
                  onChange={(e) => setEditForm({ ...editForm, make: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Model</label>
                <input
                  type="text"
                  value={editForm.model}
                  onChange={(e) => setEditForm({ ...editForm, model: e.target.value })}
                  className="w-full px-4 py-2.5 bg-slate-700/50 border border-slate-600 rounded-lg text-white"
                />
              </div>
            </div>
            
            {/* Towing Info */}
            {editForm.gcm_kg && (
              <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <p className="text-orange-300 font-medium">Towing Mode Available</p>
                    <p className="text-slate-400 mt-1">
                      When driver enables towing mode, GCM of {parseInt(editForm.gcm_kg).toLocaleString()}kg applies.
                      {editTowingRegime && editTowingRegime.regime !== editRegime.regime && (
                        <span className="text-orange-400"> This will upgrade to {editTowingRegime.label}.</span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Primary Production Toggle - only show for FRHV */}
            {parseInt(editForm.gvm_kg) >= FRHV_GVM_THRESHOLD_KG && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editForm.primary_production_eligible}
                    onChange={(e) => setEditForm({ ...editForm, primary_production_eligible: e.target.checked })}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
                  />
                  <div>
                    <span className="text-white font-medium">Primary Production Eligible</span>
                    <p className="text-xs text-slate-400 mt-0.5">Enable 160km radius for transporting primary produce (FRHV only)</p>
                  </div>
                </label>
              </div>
            )}
            
            {/* Auto-determined classification preview */}
            {editForm.gvm_kg && (
              <div className={`p-3 rounded-lg ${editRegime.color === 'amber' ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-blue-500/10 border border-blue-500/30'}`}>
                <div className="flex items-center gap-2">
                  {editRegime.regime === 'FRHV' && <AlertTriangle className="w-4 h-4 text-amber-400" />}
                  <span className="text-sm">
                    <span className="text-slate-400">Auto-classified: </span>
                    <span className={`font-medium ${editRegime.color === 'amber' ? 'text-amber-400' : 'text-blue-400'}`}>
                      {editVehicleClass}
                    </span>
                    <span className="text-slate-400"> → </span>
                    <span className={`${editRegime.color === 'amber' ? 'text-amber-400' : 'text-blue-400'}`}>
                      {editRegime.label}
                    </span>
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 justify-end">
              <button 
                type="button" 
                onClick={() => setEditingVehicle(null)} 
                className="px-4 py-2 bg-slate-700 text-white rounded-lg"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={loading} 
                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      )}

      {showAdd && (
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-medium text-white mb-4">New Vehicle</h3>
          <form onSubmit={handleAdd} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <input
                type="text"
                placeholder="Registration"
                value={form.rego}
                onChange={(e) => setForm({ ...form, rego: e.target.value })}
                className="px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm sm:text-base"
                required
                data-testid="vehicle-rego"
              />
              <input
                type="number"
                placeholder="GVM (kg) - e.g., 15000"
                value={form.gvm_kg}
                onChange={(e) => setForm({ ...form, gvm_kg: e.target.value })}
                className="px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm sm:text-base"
                required
                min="0"
                max="100000"
                data-testid="vehicle-gvm"
              />
              <input
                type="text"
                placeholder="Make"
                value={form.make}
                onChange={(e) => setForm({ ...form, make: e.target.value })}
                className="px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm sm:text-base"
                data-testid="vehicle-make"
              />
              <input
                type="text"
                placeholder="Model"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                className="px-3 sm:px-4 py-2.5 sm:py-3 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm sm:text-base"
                data-testid="vehicle-model"
              />
            </div>
            
            {/* Auto-determined classification preview */}
            {form.gvm_kg && (
              <div className={`p-3 rounded-lg ${formRegime.color === 'amber' ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-blue-500/10 border border-blue-500/30'}`}>
                <div className="flex items-center gap-2">
                  {formRegime.regime === 'FRHV' && <AlertTriangle className="w-4 h-4 text-amber-400" />}
                  <span className="text-sm">
                    <span className="text-slate-400">Auto-classified: </span>
                    <span className={`font-medium ${formRegime.color === 'amber' ? 'text-amber-400' : 'text-blue-400'}`}>
                      {formVehicleClass}
                    </span>
                    <span className="text-slate-400"> → </span>
                    <span className={`${formRegime.color === 'amber' ? 'text-amber-400' : 'text-blue-400'}`}>
                      {formRegime.label}
                    </span>
                  </span>
                </div>
              </div>
            )}
            
            <div className="flex gap-3 sm:gap-4">
              <button type="button" onClick={() => setShowAdd(false)} className="px-4 py-2 bg-slate-700 text-white rounded-lg text-sm sm:text-base">Cancel</button>
              <button type="submit" disabled={loading} className="px-4 py-2 bg-emerald-500 text-white rounded-lg disabled:opacity-50 text-sm sm:text-base" data-testid="submit-vehicle-btn">
                {loading ? 'Adding...' : 'Add Vehicle'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        {vehicles.length === 0 ? (
          <div className="col-span-full bg-slate-800/50 rounded-2xl border border-slate-700 p-6 sm:p-8 text-center text-slate-400 text-sm sm:text-base">
            No vehicles found
          </div>
        ) : (
          vehicles.map(vehicle => {
            const gvm = vehicle.gvm_kg || vehicle.weight_kg;
            const regime = getRegimeFromGVM(gvm);
            const isPrimaryProduction = vehicle.primary_production_eligible;
            return (
              <div 
                key={vehicle.vehicle_id} 
                className={`bg-slate-800/50 rounded-2xl border hover:border-slate-600 p-4 sm:p-6 transition ${
                  isPrimaryProduction ? 'border-emerald-500/50' : 'border-slate-700'
                }`}
              >
                <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                    regime.regime === 'FRHV' ? 'bg-amber-500/20' : 'bg-purple-500/20'
                  }`}>
                    <Truck className={`w-5 h-5 sm:w-6 sm:h-6 ${
                      regime.regime === 'FRHV' ? 'text-amber-400' : 'text-purple-400'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-white text-sm sm:text-base">{vehicle.rego || vehicle.registration || 'No Rego'}</p>
                    <p className="text-xs sm:text-sm text-slate-400">{vehicle.make} {vehicle.model}</p>
                  </div>
                  <button
                    onClick={() => openEdit(vehicle)}
                    className="p-2 bg-slate-700/50 hover:bg-slate-600 rounded-lg text-slate-400 hover:text-white transition"
                    data-testid={`edit-vehicle-${vehicle.vehicle_id}`}
                    title="Edit vehicle"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between flex-wrap gap-1">
                    <span className={`px-2 py-1 rounded text-xs sm:text-sm ${
                      regime.regime === 'FRHV' 
                        ? 'bg-amber-500/20 text-amber-400' 
                        : 'bg-blue-500/20 text-blue-400'
                    }`}>
                      {vehicle.vehicle_class}
                    </span>
                    {isPrimaryProduction && (
                      <span className="px-2 py-1 rounded text-xs bg-emerald-500/20 text-emerald-400">
                        160km Primary
                      </span>
                    )}
                    <span className="text-emerald-400 text-xs sm:text-sm">Active</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span>GVM: {gvm ? `${gvm.toLocaleString()} kg` : 'N/A'}</span>
                    <span className={regime.regime === 'FRHV' ? 'text-amber-400/70' : 'text-blue-400/70'}>
                      {regime.regime === 'FRHV' ? 'HVN Law' : 'Business Rules'}
                    </span>
                  </div>
                  {vehicle.gcm_kg && (
                    <div className="flex items-center justify-between text-xs text-orange-400/80 border-t border-slate-700/50 pt-1 mt-1">
                      <span>GCM: {vehicle.gcm_kg.toLocaleString()} kg</span>
                      <span className="text-orange-300/60">
                        Towing → {vehicle.towing_regime_label || getRegimeFromGVM(vehicle.gcm_kg).label}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default VehiclesPanel;
